import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import TransactionModal from "./TransactionModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function IOUModal({ open, onClose, categories, cashbooks, onSuccess, editData = null }) {

    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedCashBook, setSelectedCashBook] = useState(null);
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const [iouId, setIouId] = useState(null);
    const [iouStatus, setIouStatus] = useState(null);

    const [showTxModal, setShowTxModal] = useState(false);

    const token = sessionStorage.getItem("token");
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const role = user.role || "";

    const isAccountant = role === "Accountant";
    const isEditMode = Boolean(editData?._id);

    // ✅ FIXED CASHBOOK MATCHING (IMPORTANT)
    useEffect(() => {
        if (!selectedCategory || !cashbooks.length) {
            setSelectedCashBook(null);
            return;
        }

        const cb = cashbooks.find(c => {
            const catId =
                typeof c.category === "object"
                    ? c.category?._id
                    : c.category;

            return String(catId) === String(selectedCategory);
        });

        setSelectedCashBook(cb || null);
        fetchUsers();

    }, [selectedCategory, cashbooks]);

    const fetchUsers = async () => {

        try {

            const res = await axios.get(
                `${API_BASE_URL}/pettycash/reimbursement-users`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const filteredUsers =
                (res.data.data || []).filter(
                    user =>
                        user.role === "Farm User" ||
                        user.role === "Outlet User"
                );

            setUsers(filteredUsers);

        } catch (err) {
            console.log(err);
        }
    };

    // ================= CREATE IOU =================
    const handleCreateIOU = async () => {
        try {

            if (isAccountant) {
                if (!selectedCategory) {
                    return toast.error("Select category");
                }

                if (!selectedCashBook) {
                    return toast.error("No CashBook found");
                }

                if (!selectedUser) {
                    return toast.error("Select user");
                }
            }

            if (!amount || Number(amount) <= 0) {
                return toast.error("Enter valid amount");
            }

            const res = await axios.post(
                `${API_BASE_URL}/pettycash/transaction`,
                {
                    cash_book:
                        isAccountant
                            ?selectedCashBook._id
                            : null,
                    category:
                        isAccountant
                            ? selectedCategory
                            : null,
                    transaction_type: "IOU",
                    amount,
                    description,
                    requested_for_user:
                        isAccountant
                            ? selectedUser
                            : null
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const data = res.data.data;

            setIouId(data._id);
            setIouStatus(data.iou_status);

            toast.success("IOU Created");

            onSuccess && onSuccess();

        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Error");
        }
    };

    const handleUpdateIOU = async () => {

        try {

            if (!selectedCategory) {
                return toast.error("Select category");
            }

            if (!selectedCashBook) {
                return toast.error("No CashBook found");
            }

            if (!selectedUser) {
                return toast.error("Select user");
            }

            if (!amount || Number(amount) <= 0) {
                return toast.error("Enter valid amount");
            }

            await axios.put(
                `${API_BASE_URL}/pettycash/update-iou/${editData._id}`,
                {
                    cash_book: selectedCashBook._id,
                    category: selectedCategory,
                    amount,
                    description,
                    requested_for_user: selectedUser
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            toast.success("IOU Updated");

            onSuccess && onSuccess();

        } catch (err) {

            console.error(err);

            toast.error(
                err.response?.data?.message || "Update failed"
            );
        }
    };

    useEffect(() => {

        if (open && editData) {

            setSelectedCategory(
                editData.category?._id || ""
            );

            setSelectedUser(
                editData.requested_for_user?._id || ""
            );

            setAmount(editData.amount || "");

            setDescription(
                editData.description || ""
            );

            setIouId(editData._id);

            setIouStatus(editData.iou_status || null);
        }

    }, [open, editData]);

    // ================= RESET =================
    useEffect(() => {
        if (!open) {
            setSelectedCategory("");
            setSelectedCashBook(null);
            setSelectedUser("");
            setAmount("");
            setDescription("");
            setIouId(null);
            setIouStatus(null);
            setShowTxModal(false);
        }
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">

            <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-w-[95vw] max-h-[90vh] flex flex-col  overflow-hidden">

                {/* HEADER */}
                <div className="shrink-0 bg-green-700 text-white px-6 py-4 flex justify-between">
                    <h2>Add IOU</h2>
                    <button onClick={onClose}>×</button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar scroll-smooth">

                    {/* CATEGORY */}
                    {
                        isAccountant && (
                            <select
                                className="w-full border p-2 rounded"
                                value={selectedCategory}
                                onChange={(e) => {
                                    setSelectedCategory(e.target.value);
                                }}
                            >
                                <option value="">Select Category</option>
                                {categories.map(c => (
                                    <option key={c._id} value={c._id}>
                                        {c.category_name}
                                    </option>
                                ))}
                            </select>
                        )
                    }

                    {/* CASHBOOK */}
                    {
                        isAccountant && (
                            <input
                                value={
                                    selectedCashBook
                                        ? selectedCashBook.cash_book_code
                                        : "No CashBook Found"
                                }
                                readOnly
                                className={`w-full border p-2 rounded ${
                                    selectedCashBook ? "bg-gray-100" : "bg-red-100"
                                }`}
                            />
                        )
                    }

                    {
                        isAccountant && (
                            <div>
                                <label className="block mb-2 font-medium">
                                    Select User
                                </label>

                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    className="w-full border px-4 py-2 rounded-lg"
                                >
                                    <option value="">
                                        Select Farm / Outlet User
                                    </option>

                                    {
                                        users.map(user => (
                                            <option
                                                key={user._id}
                                                value={user._id}
                                            >
                                                {user.username} ({user.role})
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                        )
                    }

                    {/* AMOUNT */}
                    <input
                        type="number"
                        placeholder="Amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full border p-2 rounded"
                    />

                    {/* DESCRIPTION */}
                    <textarea
                        placeholder="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full border p-2 rounded"
                    />

                    {/*/!* STATUS *!/*/}
                    {/*{iouStatus && (*/}
                    {/*    <div className="bg-blue-50 p-2 rounded text-sm">*/}
                    {/*        Status: <b>{iouStatus}</b>*/}
                    {/*    </div>*/}
                    {/*)}*/}

                    {/*/!* EXPENSE BUTTON *!/*/}
                    {/*{iouStatus === "Received" ? (*/}
                    {/*    <button*/}
                    {/*        onClick={() => setShowTxModal(true)}*/}
                    {/*        className="bg-blue-600 text-white px-3 py-2 rounded"*/}
                    {/*    >*/}
                    {/*        + Add Expense*/}
                    {/*    </button>*/}
                    {/*) : iouId ? (*/}
                    {/*    <p className="text-orange-600 text-sm">*/}
                    {/*        Wait until money is <b>Received</b>*/}
                    {/*    </p>*/}
                    {/*) : null}*/}

                    {/* ACTIONS */}
                    <div className="shrink-0 flex justify-end gap-3 p-5 border-t bg-white">
                        <button
                            onClick={onClose}
                            className="bg-gray-300 px-4 py-2 rounded"
                        >
                            Cancel
                        </button>

                        {!isEditMode && !iouId && (
                            <button
                                onClick={handleCreateIOU}
                                className="bg-green-700 text-white px-4 py-2 rounded"
                            >
                                Create IOU
                            </button>
                        )}

                        {
                            isEditMode && (
                                <button
                                    onClick={handleUpdateIOU}
                                    className="bg-blue-700 text-white px-4 py-2 rounded"
                                >
                                    Update IOU
                                </button>
                            )
                        }
                    </div>

                </div>
            </div>

            {/* TRANSACTION MODAL */}
            <TransactionModal
                open={showTxModal}
                onClose={() => setShowTxModal(false)}
                selectedCashBook={selectedCashBook}
                relatedIOU={iouId}
                onSuccess={() => {
                    onSuccess && onSuccess();
                    setShowTxModal(false);
                }}
            />

        </div>
    );
}

export default IOUModal;